import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FileText, Pencil, Plus, Trash2, Upload } from 'lucide-react';
import {
  createPetRecord,
  createPetRecordUploadUrl,
  deletePetRecord,
  getPetRecordViewUrl,
  getPetRecords,
  type PetRecord,
  updatePetRecord,
  uploadPetRecordFile,
} from '../lib/api';

type PetRecordInput = Omit<PetRecord, 'id' | 'pet_id' | 'has_file' | 'created_at' | 'updated_at'>;

export default function PetDocumentsPanel({ petId, onRecordsChanged, startWithComposer = false }: { petId: string; onRecordsChanged: (records: PetRecord[]) => void; startWithComposer?: boolean }) {
  const { getAccessTokenSilently } = useAuth0();
  const [records, setRecords] = useState<PetRecord[]>([]);
  const [storageConfigured, setStorageConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRecord, setEditingRecord] = useState<PetRecord | null>(null);
  const [addingRecord, setAddingRecord] = useState(startWithComposer);
  const getToken = async () => {
    const token = await getAccessTokenSilently();
    if (!token) throw new Error('Missing access token');
    return token;
  };

  useEffect(() => {
    let isCurrent = true;
    const loadRecords = async () => {
      setLoading(true);
      setError('');
      try {
        const token = await getToken();
        const result = await getPetRecords(token, petId);
        if (!isCurrent) return;
        setRecords(result.records);
        setStorageConfigured(result.storage_configured);
      } catch (loadError) {
        if (isCurrent) setError(loadError instanceof Error ? loadError.message : 'Unable to load documents');
      } finally {
        if (isCurrent) setLoading(false);
      }
    };
    void loadRecords();
    return () => { isCurrent = false; };
  }, [getAccessTokenSilently, petId]);

  const saveRecord = async (input: PetRecordInput, record?: PetRecord) => {
    const token = await getToken();
    const { file, ...metadata } = input as PetRecordInput & { file?: File };
    let uploadedFile: { file_key: string; file_name: string; mime_type: string; size_bytes: number } | undefined;
    if (file) {
      const upload = await createPetRecordUploadUrl(token, petId, file);
      await uploadPetRecordFile(upload.upload_url, file);
      uploadedFile = { file_key: upload.file_key, file_name: file.name, mime_type: file.type, size_bytes: file.size };
    }
    if (record) {
      await updatePetRecord(token, petId, record.id, { ...metadata, ...uploadedFile });
    } else {
      if (!uploadedFile) throw new Error('Choose a document to upload');
      await createPetRecord(token, petId, { ...metadata, ...uploadedFile });
    }
    const refreshed = await getPetRecords(token, petId);
    setRecords(refreshed.records);
    setStorageConfigured(refreshed.storage_configured);
    onRecordsChanged(refreshed.records);
    setAddingRecord(false);
    setEditingRecord(null);
  };

  const openRecord = async (record: PetRecord) => {
    if (!record.has_file) return;
    const pendingWindow = window.open('', '_blank');
    try {
      const token = await getToken();
      const { view_url } = await getPetRecordViewUrl(token, petId, record.id);
      if (pendingWindow) pendingWindow.location.href = view_url;
      else window.open(view_url, '_blank', 'noopener,noreferrer');
    } catch (openError) {
      pendingWindow?.close();
      setError(openError instanceof Error ? openError.message : 'Unable to open document');
    }
  };

  const removeRecord = async (record: PetRecord) => {
    if (!window.confirm(`Remove "${record.title}"? This also deletes its uploaded file.`)) return;
    try {
      const token = await getToken();
      await deletePetRecord(token, petId, record.id);
      const remaining = records.filter((item) => item.id !== record.id);
      setRecords(remaining);
      onRecordsChanged(remaining);
    } catch (removeError) {
      setError(removeError instanceof Error ? removeError.message : 'Unable to remove document');
    }
  };

  return <section className="pet-documents-panel">
    <div className="documents-heading"><div><h3>Documents</h3><p>{records.length} {records.length === 1 ? 'record' : 'records'}</p></div><button className="button button-primary" disabled={!storageConfigured} onClick={() => setAddingRecord(true)}><Plus size={16} /> Add document</button></div>
    {!storageConfigured && <p className="storage-notice">Uploads need Google Cloud Storage configured for this environment. Existing records remain available.</p>}
    {error && <p className="form-error" role="alert">{error}</p>}
    {loading ? <p className="documents-empty">Loading documents...</p> : records.length === 0 ? <p className="documents-empty">No documents have been added for this pet.</p> : <div className="document-list">{records.map((record) => <article className="document-row" key={record.id}>
      <button className="document-open" type="button" onClick={() => void openRecord(record)} disabled={!record.has_file} aria-label={record.has_file ? `Open ${record.title}` : `${record.title} has no attached file`}><span className="document-icon"><FileText size={19} /></span><span className="document-copy"><strong>{record.title}</strong><small>{record.file_name || 'No attached file'} · {new Date(record.date).toLocaleDateString()}</small></span></button>
      <div className="document-actions"><button className="icon-button" onClick={() => setEditingRecord(record)} aria-label={`Edit ${record.title}`} title="Edit or replace document"><Pencil size={16} /></button><button className="icon-button danger-icon" onClick={() => void removeRecord(record)} aria-label={`Remove ${record.title}`} title="Remove document"><Trash2 size={16} /></button></div>
    </article>)}</div>}
    {(addingRecord || editingRecord) && <PetRecordForm key={editingRecord?.id || 'new'} record={editingRecord || undefined} onClose={() => { setAddingRecord(false); setEditingRecord(null); }} onSave={saveRecord} />}
  </section>;
}

function PetRecordForm({ record, onClose, onSave }: { record?: PetRecord; onClose: () => void; onSave: (input: PetRecordInput, record?: PetRecord) => Promise<void> }) {
  const [title, setTitle] = useState(record?.title || '');
  const [recordType, setRecordType] = useState(record?.record_type || 'document');
  const [date, setDate] = useState(record?.date.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState(record?.description || '');
  const [provider, setProvider] = useState(record?.provider_name || '');
  const [file, setFile] = useState<File | undefined>();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave({
        title,
        record_type: recordType,
        date,
        description: description || null,
        provider_name: provider || null,
        file_name: file?.name || record?.file_name || null,
        mime_type: file?.type || record?.mime_type || null,
        size_bytes: file?.size || record?.size_bytes || null,
        has_file: Boolean(file || record?.has_file),
        file_key: '',
        ...(file ? { file } : {}),
      } as PetRecordInput & { file?: File }, record);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save document');
    } finally {
      setSaving(false);
    }
  };

  return <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="document-form-title"><div className="modal document-form-modal">
    <div className="modal-heading"><div><p className="eyebrow">Pet documents</p><h2 id="document-form-title">{record ? 'Edit document' : 'Add document'}</h2></div><button className="icon-button" onClick={onClose} aria-label="Close dialog">×</button></div>
    <div className="form-grid"><label>Title<input value={title} onChange={(event) => setTitle(event.target.value)} required placeholder="e.g. Annual wellness exam" /></label><label>Type<select value={recordType} onChange={(event) => setRecordType(event.target.value)}><option value="document">Document</option><option value="vaccination">Vaccination</option><option value="surgery">Surgery</option><option value="lab_result">Lab result</option><option value="general_note">General note</option></select></label><label>Date<input type="date" value={date} onChange={(event) => setDate(event.target.value)} required /></label><label>Provider<input value={provider} onChange={(event) => setProvider(event.target.value)} placeholder="Clinic or provider" /></label><label className="full-field">Notes<textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Optional details" /></label><label className="full-field">{record ? 'Replace file (optional)' : 'File'}<input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.docx,application/pdf,image/jpeg,image/png,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document" onChange={(event) => setFile(event.target.files?.[0])} required={!record} /><small className="upload-help">PDF, JPG, PNG, WebP, or DOCX · Up to 25 MB{record?.file_name ? ` · Current: ${record.file_name}` : ''}</small></label></div>
    {error && <p className="form-error" role="alert">{error}</p>}
    <div className="modal-actions"><button className="button button-secondary" onClick={onClose}>Cancel</button><button className="button button-primary" disabled={saving || !title.trim() || !date || (!record && !file)} onClick={() => void submit()}><Upload size={16} /> {saving ? 'Saving...' : record ? 'Save changes' : 'Upload document'}</button></div>
  </div></div>;
}